/**
 * @file components/log/WeightProgressCharts.jsx
 * @description Weight-progress analytics: composes the stat cards and the
 * individual charts (weight trend, calorie compliance, weekly deficit).
 */

import { memo, useMemo } from "react";
import { buildWeeklyDeficits } from "../../utils/weeklyReview";
import { calculateTrendLine, mergeChartData } from "./weightCharts/chartUtils";
import { ChartCard, StatCard } from "./weightCharts/chartUI";
import { WeightTrendChart } from "./weightCharts/WeightTrendChart";
import { CalorieComplianceChart } from "./weightCharts/CalorieComplianceChart";
import { WeeklyDeficitChart } from "./weightCharts/WeeklyDeficitChart";

function WeightProgressCharts({
  weightLogs = [],
  meals = [],
  profileData = {},
}) {
  // Memoize the heavy transforms so we don't recompute on every parent render.
  const chartData = useMemo(
    () => calculateTrendLine(mergeChartData(weightLogs, meals, profileData)),
    [weightLogs, meals, profileData],
  );

  // Weekly deficit: goal vs what the user actually achieved.
  const { weeks, dailyGoalDeficit } = useMemo(
    () => buildWeeklyDeficits(meals, profileData),
    [meals, profileData],
  );

  const weeklyDeficitData = useMemo(
    () =>
      weeks.map((w) => ({
        label: w.label,
        actual: w.actualDeficit,
        goal: w.goalDeficit,
        daysLogged: w.daysLogged,
        estKgChange: w.estKgChange,
      })),
    [weeks],
  );

  if (!weightLogs.length)
    return (
      <div className="text-center py-20 bg-white/5 rounded-3xl border border-dashed border-white/10">
        <p className="text-gray-500">
          Log your weight to unlock progress insights. 📉
        </p>
      </div>
    );

  const currentWeight = chartData[chartData.length - 1]?.weight || 0;
  const startWeight = chartData[0]?.weight || 0;
  const weightLoss = (startWeight - currentWeight).toFixed(1);

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      {/* Stats Summary - Matches your existing dashboard card style */}
      <div className="grid grid-cols-3 gap-3">
        <StatCard
          label="Total Lost"
          value={weightLoss}
          unit="kg"
          color="text-emerald-400"
        />
        <StatCard
          label="Current"
          value={currentWeight}
          unit="kg"
          color="text-white"
        />
        <StatCard
          label="To Goal"
          value={(currentWeight - profileData.goalWeight).toFixed(1)}
          unit="kg"
          color="text-amber-400"
        />
      </div>

      <ChartCard title="Weight Trend vs Goal">
        <WeightTrendChart data={chartData} />
      </ChartCard>

      <ChartCard title="Calorie Compliance">
        <CalorieComplianceChart data={chartData} />
      </ChartCard>

      {weeklyDeficitData.length > 0 && (
        <ChartCard title="Weekly Deficit vs Goal" minHeight={320}>
          <WeeklyDeficitChart
            data={weeklyDeficitData}
            dailyGoalDeficit={dailyGoalDeficit}
          />
        </ChartCard>
      )}
    </div>
  );
}

export default memo(WeightProgressCharts);
